/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai.rules

import i18n.StringKey
import i18n.tr
import kotlinx.serialization.Serializable

/**
 * 50/30/20 budget framework bucket assignments — Phase 3.1.
 *
 * Each spending category is classified into one of four buckets, used by the
 * BudgetHealthAggregator (Phase 3.2) to compute the user's actual 50/30/20
 * split and surface deviations to the LLM.
 *
 * The engine is OPINIONATED: ship a sensible default for every Plaid PFC
 * primary code. Users override per-category via the Budget Planner UI; the
 * override is stored in `budget_category_classifications` and consulted
 * before falling back to defaults.
 *
 * The framework is a benchmark, not a rule. The engine just classifies —
 * judgment about whether the user's actual ratios are "healthy" lives in the
 * aggregator + the LLM prompt section.
 */
object BudgetClassificationEngine {

    /**
     * Classify a category for the 50/30/20 framework.
     *
     * @param plaidPrimary the Plaid PFC primary code (e.g. "FOOD_AND_DRINK").
     *   Used as the default when no user override exists.
     * @param plaidDetailed optional Plaid detailed code, used for sub-category
     *   splits (e.g. groceries are NEEDS but restaurants are WANTS within
     *   FOOD_AND_DRINK).
     * @param userOverride optional explicit bucket the user set via the
     *   Budget Planner. Wins over the default when present.
     */
    fun classify(
        plaidPrimary: String?,
        plaidDetailed: String? = null,
        userOverride: BudgetBucket? = null,
    ): BudgetBucket {
        userOverride?.let { return it }
        val primary = plaidPrimary?.uppercase()?.replace(" ", "_") ?: return BudgetBucket.UNCLASSIFIED
        val detailed = plaidDetailed?.uppercase()?.replace(" ", "_")
        return classifyByPlaid(primary, detailed)
    }

    /**
     * Return the canonical default bucket for a Plaid primary, ignoring user
     * overrides. Used by the Budget Planner UI to display the default before
     * the user has set an override.
     */
    fun defaultFor(plaidPrimary: String?, plaidDetailed: String? = null): BudgetBucket =
        classify(plaidPrimary, plaidDetailed, userOverride = null)

    /**
     * The single bucket-resolution path. Both BudgetHealthAggregator (which
     * feeds the AI cards) and BucketFlowBuilder (which feeds the dashboard
     * Sankey) call this, so the two surfaces cannot disagree about what
     * bucket a transaction belongs to.
     *
     * Precedence: exact category override → detailed-code override → engine
     * default for the (primary, detailed) pair.
     */
    fun effectiveBucket(
        categoryCode: String,
        detailedCode: String?,
        userClassifications: Map<String, BudgetBucket>,
    ): BudgetBucket {
        userClassifications[categoryCode]?.let { return it }
        if (detailedCode != null && categoryCode != detailedCode) {
            userClassifications[detailedCode]?.let { return it }
        }
        return classify(categoryCode, detailedCode)
    }

    private fun classifyByPlaid(primary: String, detailed: String?): BudgetBucket {
        // Income / transfers — these aren't spending and don't count toward
        // any bucket. The aggregator filters them upstream too, but defending
        // in depth is cheap.
        if (primary == "INCOME") return BudgetBucket.IGNORE
        if (primary == "TRANSFER_IN") return BudgetBucket.IGNORE
        if (primary == "TRANSFER_OUT") {
            // Tagged goal contributions count as SAVINGS. Untagged outgoing
            // transfers are ambiguous (could be cash to another account, could
            // be paying a friend back); the aggregator decides based on goal
            // tagging, not the bucket default. We classify the TYPE as
            // SAVINGS-adjacent so the user sees the right default in the
            // planner UI.
            return BudgetBucket.SAVINGS
        }

        // Food & drink — split by detailed code. Groceries are needs;
        // restaurants/coffee/bars are wants.
        if (primary == "FOOD_AND_DRINK") {
            return when {
                detailed == null -> BudgetBucket.WANTS // safer default
                detailed.contains("GROCERIES") -> BudgetBucket.NEEDS
                detailed.contains("RESTAURANT") -> BudgetBucket.WANTS
                detailed.contains("FAST_FOOD") -> BudgetBucket.WANTS
                detailed.contains("COFFEE") -> BudgetBucket.WANTS
                detailed.contains("BAR") || detailed.contains("ALCOHOL") -> BudgetBucket.WANTS
                detailed.contains("VENDING") -> BudgetBucket.WANTS
                else -> BudgetBucket.WANTS
            }
        }

        // Loans — mortgage / student loans / car loans are all NEEDS (you
        // contracted them; you can't easily not pay).
        if (primary == "LOAN_PAYMENTS") return BudgetBucket.NEEDS

        // Rent and utilities — almost always NEEDS regardless of detailed code.
        if (primary == "RENT_AND_UTILITIES") return BudgetBucket.NEEDS

        // Government & non-profit — taxes are NEEDS, donations are technically
        // wants. We default to NEEDS because most users budget taxes there,
        // and donations are routinely intentional + recurring (treat as a
        // commitment, not a discretionary).
        if (primary == "GOVERNMENT_AND_NON_PROFIT") return BudgetBucket.NEEDS

        // Medical — NEEDS.
        if (primary == "MEDICAL") return BudgetBucket.NEEDS

        // Transportation — gas, parking, transit, tolls are all needs for the
        // typical user who commutes. Rideshare can be discretionary but we
        // default to NEEDS conservatively.
        if (primary == "TRANSPORTATION") return BudgetBucket.NEEDS

        // Insurance falls under GENERAL_SERVICES in Plaid's taxonomy. Treat
        // insurance & childcare & legal as NEEDS; the rest as WANTS.
        if (primary == "GENERAL_SERVICES") {
            if (detailed != null) {
                if (detailed.contains("INSURANCE")) return BudgetBucket.NEEDS
                if (detailed.contains("CHILDCARE")) return BudgetBucket.NEEDS
                if (detailed.contains("LEGAL") || detailed.contains("ACCOUNTING")) return BudgetBucket.NEEDS
            }
            return BudgetBucket.WANTS
        }

        // Home improvement — default WANTS, but repair/maintenance detailed
        // codes lean NEEDS.
        if (primary == "HOME_IMPROVEMENT") {
            return when {
                detailed == null -> BudgetBucket.WANTS
                detailed.contains("REPAIR") || detailed.contains("MAINTENANCE") -> BudgetBucket.NEEDS
                else -> BudgetBucket.WANTS
            }
        }

        // Bank fees — irritating but typically unavoidable. NEEDS.
        if (primary == "BANK_FEES") return BudgetBucket.NEEDS

        // Personal care — gym, spa, haircuts, cosmetics. WANTS by default,
        // though one could argue a haircut is a need; let the user override.
        if (primary == "PERSONAL_CARE") return BudgetBucket.WANTS

        // Entertainment, Travel, General merchandise — classic WANTS.
        if (primary == "ENTERTAINMENT") return BudgetBucket.WANTS
        if (primary == "TRAVEL") return BudgetBucket.WANTS
        if (primary == "GENERAL_MERCHANDISE") return BudgetBucket.WANTS

        // Unrecognized — surface as UNCLASSIFIED so the user can bucket it.
        return BudgetBucket.UNCLASSIFIED
    }
}

@Serializable
enum class BudgetBucket(val wire: String) {
    /** 50% — obligations, fixed costs, things you can't easily not pay. */
    NEEDS("NEEDS"),

    /** 30% — discretionary, lifestyle, "nice to have". */
    WANTS("WANTS"),

    /** 20% — goal contributions, retirement, emergency fund. */
    SAVINGS("SAVINGS"),

    /** Income, internal transfers, anything that doesn't count toward the 100%. */
    IGNORE("IGNORE"),

    /** No default classification — the user should pick one. */
    UNCLASSIFIED("UNCLASSIFIED");

    /**
     * Localized, user-facing name for this bucket. Single source of truth —
     * never render `BudgetBucket.name` (the raw English enum constant) in UI.
     */
    fun displayLabel(): String = when (this) {
        NEEDS -> StringKey.BUDGET_BUCKET_NEEDS.tr()
        WANTS -> StringKey.BUDGET_BUCKET_WANTS.tr()
        SAVINGS -> StringKey.BUDGET_BUCKET_SAVINGS.tr()
        IGNORE -> StringKey.BUDGET_BUCKET_IGNORE.tr()
        UNCLASSIFIED -> StringKey.BUDGET_BUCKET_UNCLASSIFIED.tr()
    }

    companion object {
        fun fromWire(s: String?): BudgetBucket =
            entries.firstOrNull { it.wire == s } ?: UNCLASSIFIED
    }
}
